import React, { useState } from "react";
import { Box, Button, useTheme } from "@mui/material";
import { DocumentActionType, DocumentHistory, ExportDocument, ExportDocumentType, HistoryEntry, Sale, ThemeColor } from "../../../system/Domain";
import TextBlock from "Components/Layout/TextBlock";
import List from "Components/List";
import ListItem from "Components/ListItem";
import ListItemAction from "Components/ListItemAction";
import ListItemActions from "Components/ListItemActions";
import ListItemChip from "Components/ListItemChip";
import ListItemChips from "Components/ListItemChips";
import ListItemContent from "Components/ListItemContent";
import { Clear, CloudUploadSharp, Delete } from "@mui/icons-material";
import axios from "axios";
import getFiles from "Dialogs/getFiles";
import executeWithProgressDialog from "Dialogs/executeWithProgressDialog";
import TextGroup from "../TextGroup";
import formatDateTime from "system/formatDateTime";
import TextLine from "../TextLine";
import fileDownload from "js-file-download";
import ask from "Dialogs/ask";

interface Props {
	sale: Sale
	document: ExportDocument
	documentHistory: HistoryEntry<DocumentHistory>[]
	modify: boolean
	mandatory?: boolean
	setSale: (s: Sale) => void
	orderId: number
}

export const translateExportDocument = (value: ExportDocumentType): string => {
	switch (value) {
		case "ExportProof":
			return "Exportnachweis";
		case "ArrivalConfirmation":
			return "Gelangensbestätigung";
		case "KycConfirmation":
			return "KYC Nachweis"
		case "Other":
			return "Weiteres Dokument";
		default:
			return value;
	}
};

export const translateFileAction = (value: DocumentActionType): string => {
	switch (value) {
		case "Add":
			return "Hochgeladen";
		case "Delete":
			return "Gelöscht";
		default:
			return value;
	}
};

type Mode = "view" | "delete" | "history";

const SaleExportDocument = ({ sale, document, documentHistory, modify, mandatory = false, setSale, orderId }: Props) => {
	const theme = useTheme();
	const [mode, setMode] = useState<Mode>("view");

	const uploadFile = async () => {
		const result = await getFiles("Export Dokument hochladen", {
			multiple: false
		});

		if (result.ok) {
			await executeWithProgressDialog(async progress => {
				let formData = new FormData();
				formData.append("file", result.files[0]);
				formData.append("documentType", document.documentType);

				const { data: newSale } = await axios.post<Sale>(`/api/sales/${sale.id}/export/documents`,
					formData, {
						headers: {
							'Content-Type': 'multipart/form-data'
						},
						onUploadProgress: evt => {
							const completed = Math.ceil((evt.loaded / evt.total) * 100);
							progress(completed);
						}
					}
				);

				setSale(newSale);
			});
		}

		setMode("view");
	};

	const deleteDocument = async () => {
		if (!document.file?.hash) {
			return;
		}

		try {
			const { data: updatedSale } = await axios.delete<Sale>(`/api/sales/${sale.id}/export/documents/${document.file.hash}`)

			setSale(updatedSale);
		} finally {
			setMode("view");
		}
	};

	const downloadFile = async () => {
		if (!document.file?.hash) {
			return;
		}

		const answer = await ask("Wollen Sie das Dokument herunterladen?");
		if (answer.yes) {
			const response = await axios.get(`/api/storage/${document.file?.hash}`, {
				responseType: "blob"
			});

			fileDownload(response.data, document.file?.filename);
		}
	};

	const canUpload = !!!document.file;
	const canDelete = !!document.file;
	const isUploaded = !!document.file?.hash;
	const isAction = mode === "delete";

	let itemColor: ThemeColor = "info";
	if (mode === "delete") {
		itemColor = "error";
	}

	return (
		<List>
			<ListItem color={itemColor}>
				<ListItemActions>
					{isAction && (
						<ListItemAction icon={<Clear />} onClick={() => setMode("view")} />
					)}
					{!isAction && (
						<>
							<ListItemAction
								icon={<CloudUploadSharp />}
								disabled={!modify || !canUpload || isAction}
								onClick={uploadFile} />
							<ListItemAction
								icon={<Delete />}
								disabled={!modify || !canDelete || isAction}
								onClick={() => setMode("delete")} />
						</>
					)}
				</ListItemActions>
				<ListItemContent>
					<TextBlock
						flat
						primary={translateExportDocument(document.documentType)}
						secondary={document.file?.filename}
					/>
					{mode === "delete" && (
						<Box mt={2} color={theme.palette.error.light}>
							<Button
								color="inherit"
								variant="text"
								onClick={deleteDocument}
								startIcon={<Delete />}
							>
								Dokument löschen
							</Button>
						</Box>
					)}
					{mode === "history" && (
						<Box mt={3} mb={1}>
							<TextGroup title="Dokumenthistorie" gap="wide">
								{documentHistory
									.map((h) => (
										<Box display="flex" flexDirection="row">
											<TextLine
												label={formatDateTime(h.date)}
												color={h.value.actionType === "Add" ? "success" : "error"}
												value={
													<Box fontStyle="bold">{translateFileAction(h.value.actionType)}</Box>
												}
											/>
											<Box ml="auto">
												<TextLine
													label={h.createdBy.name}
													value={null}
												/>
											</Box>
										</Box>
									))}
							</TextGroup>
						</Box>
					)}
					<ListItemChips>
						{(mandatory || isUploaded) && (
							<ListItemChip
								label={isUploaded ? "Hochgeladen" : "Erwartet"}
								color={isUploaded ? "success" : "info"}
								onClick={isUploaded ? downloadFile : null}
							/>
						)}

						{documentHistory.length > 0 && (
							<ListItemChip
								label="Historie"
								color="primary"
								active={mode === "history"}
								onClick={() => setMode((m) => (m === "history" ? "view" : "history"))}
							/>
						)}
					</ListItemChips>
				</ListItemContent>
			</ListItem>
		</List>
	);
};

export default SaleExportDocument;

